import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { ChatApi } from '@/api/chat';
import type { ChatStreamApi } from '@/api/chat-stream';
import type { SessionHistoryApi } from '@/api/session-history';
import { ApiError } from '@/utils/errors';
import { classify, isRestoreHopeless } from './error-policy';
import { readAndClearEcho } from './echo-handoff';
import { hydrateSession } from './session-hydrate';
import type { ProviderPayload, SessionHistoryResponse } from '@/types/chat';
import {
  makeAssistantMessage,
  makeEchoMessage,
  makeFailedMessage,
  makeStreamingMessage,
  makeUserMessage,
  makeWelcomeMessage,
  type ChatMessage,
  type StreamBlock,
} from './messages';

const RESTORE_TIMEOUT_MS = 5_000;

export interface UseChatSessionOptions {
  initialSessionId?: string;
  onSessionIdChange?: (sessionId: string | null) => void;
  disableOverviewEcho?: boolean;
  /**
   * Optional streaming API. When provided, send() uses SSE streaming.
   * When omitted, falls back to the non-streaming api.send().
   */
  streamApi?: ChatStreamApi;
  /** 会话历史。后端是唯一的历史真相——本地不再缓存任何消息。 */
  historyApi?: SessionHistoryApi;
}

export interface UseChatSessionResult {
  messages: ChatMessage[];
  sessionId: string | null;
  /** True while an initial session's history is being fetched from the backend. */
  initializing: boolean;
  sending: boolean;
  error: Error | null;
  /** Seconds the host should wait before retrying, derived from RateLimitError. Null otherwise. */
  retryAfterSeconds: number | null;
  /** 从后端 `referenced` 还原出来的 session 级 payload，供 ChatWidget 合并。 */
  restoredPayload: ProviderPayload;
  /**
   * True when restoring history failed for transport reasons — not
   * SESSION_NOT_FOUND, and not a session-level error (classify(error).banner)
   * such as an expired key or rate limit, which is reported via `error`
   * alone so ChatWidget's session banner is the only thing that fires.
   */
  restoreFailed: boolean;
  send: (text: string) => Promise<void>;
  abort: () => void;
  /** Re-runs the restore effect — wired to the retry button on the restore-failed banner. */
  reloadHistory: () => void;
  /** Retries a failed message — wired to the retry button on its failed bubble. */
  retry: (failedMessageId: string) => Promise<void>;
}

export function useChatSession(
  api: ChatApi,
  options: UseChatSessionOptions = {},
): UseChatSessionResult {
  const { initialSessionId, onSessionIdChange, disableOverviewEcho, streamApi, historyApi } =
    options;
  const shouldRestore = Boolean(initialSessionId && historyApi);

  const [sessionId, setSessionId] = useState<string | null>(initialSessionId ?? null);
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    shouldRestore ? [] : seedInitialMessages(disableOverviewEcho),
  );
  const [initializing, setInitializing] = useState(shouldRestore);
  const [restoredPayload, setRestoredPayload] = useState<ProviderPayload>(() => ({
    products: [],
    blogs: [],
    pages: [],
  }));
  const [restoreFailed, setRestoreFailed] = useState(false);
  // banner 上的「重试」通过递增 nonce 重跑恢复 effect。
  const [reloadNonce, setReloadNonce] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 流式路径在 runStreaming 内部通过 setError 报错（SSE error 事件走的是
  // HTTP 200 正常返回，不会抛异常），attempt() 却需要在 runStreaming 返回
  // 后立刻同步判断这次调用是否失败。React state 更新不保证同步可读，
  // 所以额外维护一个 ref 当「立刻能读」的真相来源，每次 setError 都同步写它。
  const errorRef = useRef<Error | null>(null);
  function recordError(e: Error | null): void {
    errorRef.current = e;
    setError(e);
  }

  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const abortRef = useRef<AbortController | null>(null);
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // 一次性清理：这个分支已经不再往 calebasse_chat_history:* 写任何东西了
  // （历史现在只活在后端），但老版本写过的记录还留在老用户的浏览器里，
  // 不会再被读到，也永远没人删——不清就是无限期占着 localStorage 配额。
  // 挂载时扫一遍顺手删掉。try/catch 保护 localStorage 不可用（隐私模式、
  // 配额耗尽等）的情况——清理本身绝不能把挂载搞挂，被删掉的
  // history-storage.ts 里的每个函数都是同样的写法。不要碰
  // calebasse_chat_echo（sessionStorage，回声交接用）或
  // calebasse_floating_button_position_v1——两个都是仍在用的功能。
  useEffect(() => {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith('calebasse_chat_history'))
        .forEach((key) => localStorage.removeItem(key));
    } catch {
      // localStorage 不可用，没什么好清的。
    }
  }, []);

  // 宿主常传内联箭头函数当 onSessionIdChange，每次宿主重渲染身份都会变。
  // 恢复 effect 若把它放进依赖数组，宿主一重渲染就会把已完成的恢复重跑一遍，
  // 半路把正在进行的对话换成服务端历史。改存 ref，effect 只在挂载时读最新值。
  const onSessionIdChangeRef = useRef(onSessionIdChange);
  useEffect(() => {
    onSessionIdChangeRef.current = onSessionIdChange;
  }, [onSessionIdChange]);

  // 恢复只应该真正落地一次；之后唯一能让它重跑的是 reloadHistory() 显式把
  // 这个 ref 拨回 false 再递增 nonce。没有这道闸门，任何和恢复无关的依赖
  // 抖动都可能让 effect 重入，把已经完成的恢复再跑一次。
  //
  // 闩必须在「结果落地」时才拨上，不能在「发起请求」时就拨上：React 18
  // StrictMode 会同步地把 mount effect 跑一遍、清理、再跑一遍——如果闩在
  // 请求发起时就置 true，第二次调用会被闸门拦下不发请求，而第一次调用的
  // promise settle 时又会因为 cleanup 设的 active=false 而被 catch/then 的
  // 早退挡住，两边都不落地，`initializing` 永远卡在 true。把闩挪到
  // then/catch 内部——只有第一次真正跑到底的调用才会把它拨上——第二次
  // 调用发起的新请求就能正常完成并落闩，StrictMode 下退化成「多打一次
  // 无害的重复请求」，和这个效果原本对幂等 effect 的预期一致。
  const hasRestoredRef = useRef(false);
  const reloadHistory = useCallback(() => {
    hasRestoredRef.current = false;
    setReloadNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!shouldRestore || !initialSessionId || !historyApi) return;
    if (hasRestoredRef.current) return;

    let active = true;
    const ctrl = new AbortController();
    setInitializing(true);
    setRestoreFailed(false);
    // 清掉上一次失败留下的 banner——不然重试成功后横幅还挂在界面上。
    recordError(null);

    historyApi
      .getMessages(initialSessionId, { signal: ctrl.signal, timeoutMs: RESTORE_TIMEOUT_MS })
      .then((response) => {
        if (!active || ctrl.signal.aborted) return;
        hasRestoredRef.current = true;

        // 产品决策（2026-08-11）：挂载时如果后端说这个 session 已经不可
        // resume（`resumable === false`），不渲染拉回来的历史，直接按
        // 「没救了」那条分支处理——开一个全新的会话。这是刻意覆盖掉
        // 「resumable 字段目前不代表数据真的丢了」这个事实：live testing
        // 确认过，一个 `resumable: false` 的 session 这时候历史 GET 仍然
        // 拿得到完整消息、仍然接受新消息、LLM 也仍然带着上下文（问一句
        // 追问能拿到有上下文的回答）。也就是说这里会经常性地把一段其实
        // 还能正常工作、带着完整上下文的对话直接扔掉——在这个 60s 无操作
        // 就置 resumable=false 的开发环境 TTL 下尤其频繁。这不是漏洞，是
        // 产品要求的取舍：重新打开页面时宁可看到一个保证干净的新会话，
        // 也不要一个后端随时可能真正过期的旧会话。
        //
        // 只影响挂载路径——retry() 里的探活复用的是同一个 historyApi，
        // 但那是会话进行到一半时才会跑到的代码，用户当时可能只是停顿了
        // 一分钟，探活对着同一个 resumable:false 结果丢弃 session 会把一
        // 段正在进行的对话冲掉，不是「重新打开页面」这个场景本身要求的
        // 行为，所以探活分支不做这个判断。
        //
        // 缺失/undefined 一律按 true 处理——不能因为某个后端部署漏填了
        // 这个字段就把一个原本正常的会话当成过期扔掉，只有明确的
        // `false` 才触发丢弃。
        if (response.resumable === false) {
          // 只有当前会话仍然就是这次恢复的 initialSessionId 时才丢它——
          // 理由和下面「没救了」分支里同一处判断一致：这次请求飞在半空
          // 时，一次静默恢复可能已经把 sessionIdRef 挪到了别的、活着的
          // session 上，这里绝不能把后端仍然认识的会话清掉。
          if (sessionIdRef.current === initialSessionId) {
            sessionIdRef.current = null;
            setSessionId(null);
            onSessionIdChangeRef.current?.(null);
          }
          // 挂载路径下 messagesRef.current 按构造为空（shouldRestore 时
          // 初始 state 就是 []），这个判断恒真；写出来是为了和下面「没
          // 救了」分支保持完全一致的形状，避免这条分支以后被复制到别处
          // 复用时漏掉这道防线。
          if (messagesRef.current.length === 0) {
            const fresh = seedInitialMessages(disableOverviewEcho);
            messagesRef.current = fresh;
            setMessages(fresh);
          }
          return;
        }

        const { messages: restored, payload } = hydrateSession(response);
        // seedInitialMessages 在这条路径上不会被调用，但待处理的 echo 仍然
        // 需要被消费一次，否则它会残留在 sessionStorage 里，冒到同一个 tab
        // 里后面一次不相关的全新会话中。
        if (!disableOverviewEcho) readAndClearEcho();
        // 挂载路径下 messagesRef.current 按构造为空（shouldRestore 时初始
        // state 就是 []），reattachPending 在那条路径上是纯粹的 no-op。
        // 只有 reloadHistory() 触发的重跑——这次成功发生在一段已经聊了
        // 几轮、其中可能有独立失败消息的对话之后——才会真的挂回东西。
        const reconciled = reattachPending(restored, messagesRef.current);
        messagesRef.current = reconciled;
        setMessages(reconciled);
        setRestoredPayload(payload);
      })
      .catch((e: unknown) => {
        if (!active || ctrl.signal.aborted) return;
        hasRestoredRef.current = true;

        if (isRestoreHopeless(e)) {
          // 历史 GET 是 session 作用域的：SESSION_NOT_FOUND、裸 404（没部署
          // 这条路由 / 路径不对）、410（已清理）、422（比如非 UUID 的
          // initialSessionId）——不管具体原因是什么，原样重发同一个 GET
          // 都不会有不同结果。静默开新会话，不打扰用户，也不会让用户
          // 卡在一条「重试」注定失败的 banner 上。session_id 无论如何都要
          // 丢——但气泡只在「没有东西可丢」（挂载路径，列表按构造为空）
          // 时才重新播种。reloadHistory() 触发的重跑可能发生在用户已经
          // 聊了几轮之后：这时不重播种，正是「保留气泡、悄悄换一个新
          // 会话」这个设计意图本身，不是漏洞。
          //
          // 只有当前会话仍然就是刚才 404/410/422 的那个 id 时才丢它——
          // reloadHistory() 重跑的是挂载时的 initialSessionId，但如果这次
          // 请求飞在半空的时候，一次静默恢复（见 attempt() 里的
          // silentNewSession 分支）已经把 sessionIdRef 挪到了别的、活着的
          // session 上，这里绝不能把后端仍然认识的会话清掉——宿主会因此
          // 平白忘记一段其实还在的对话。onSessionIdChange 通知同样收进
          // 这个判断里，没发生的清空不该通知宿主。
          if (sessionIdRef.current === initialSessionId) {
            sessionIdRef.current = null;
            setSessionId(null);
            onSessionIdChangeRef.current?.(null);
          }
          if (messagesRef.current.length === 0) {
            const fresh = seedInitialMessages(disableOverviewEcho);
            messagesRef.current = fresh;
            setMessages(fresh);
          }
          return;
        }

        // 只在没有东西可丢的时候才播种欢迎气泡——即挂载路径，此时列表按
        // 构造为空。reloadHistory() 触发的重跑如果发生在一段真实对话之后
        // （用户在 banner 挂着的时候正常聊了几轮），这里绝不能把它们换成
        // 一条孤零零的欢迎气泡：那是把用户自己点「重试」的后果，变成把
        // 对话冲掉。
        if (messagesRef.current.length === 0) {
          const fresh = seedInitialMessages(disableOverviewEcho);
          messagesRef.current = fresh;
          setMessages(fresh);
        }

        if (classify(e).banner) {
          // historyApi 和其它接口走同一条 client → ensureOk →
          // mapHttpStatusToError 链路：401/429 会被映射成 UNAUTHORIZED /
          // RATE_LIMITED，这两个 code 的 classify().banner 都是 true。这种
          // 失败是会话级问题——重新拉一次历史解决不了鉴权失效或限流——应该
          // 只由 ChatWidget 里 `classify(session.error).banner` 那个 session
          // banner 报告。这里不设 restoreFailed，两个 banner 才真正互斥：
          // 不是靠视图层再加一道判断，是从这里开始就只往一头走。
          recordError(e as Error);
          return;
        }

        // 传输故障不是会话失效的证据——保住 session_id，让用户能重试。
        setRestoreFailed(true);
        recordError(e as Error);
      })
      .finally(() => {
        if (!active) return;
        setInitializing(false);
      });

    return () => {
      active = false;
      ctrl.abort();
    };
  }, [disableOverviewEcho, historyApi, initialSessionId, reloadNonce, shouldRestore]);

  function abort(): void {
    abortRef.current?.abort();
    abortRef.current = null;
  }

  async function send(text: string): Promise<void> {
    if (initializing || !text.trim()) return;

    recordError(null);
    setSending(true);

    const userMsg = makeUserMessage(text);
    const messagesWithUser = [...messagesRef.current, userMsg];
    messagesRef.current = messagesWithUser;
    setMessages(messagesWithUser);

    try {
      const ok = await attempt(text, messagesWithUser, true);
      // 有正回应就说明后端可达——「无法加载对话」的横幅（如果还挂着）
      // 已经过时了，不该继续压在一段明摆着正常工作的对话上面。见
      // ChatWidget.tsx 里两条 banner 那段注释。
      if (ok) setRestoreFailed(false);
    } finally {
      abortRef.current = null;
      setSending(false);
      // Strip any lingering streaming bubble (only present if aborted / errored)
      setMessages((prev) => prev.filter((m) => m.type !== 'streaming'));
    }
  }

  /**
   * 发一次请求。allowSilentRetry 为 true 时，若后端回 SESSION_NOT_FOUND，
   * 就丢掉 session_id 再发一次——用户全程无感。第二次不再允许静默重发，
   * 避免后端持续 404 时打转。
   *
   * 返回值表示这一轮最终是否拿到了助手的回应（true），供调用方判断要不要
   * 清掉 restoreFailed——中止（AbortError）和落到失败气泡/banner 都算
   * false，只有真正的回应算成功。
   */
  async function attempt(
    text: string,
    baseMessages: ChatMessage[],
    allowSilentRetry: boolean,
  ): Promise<boolean> {
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      if (streamApi) {
        await runStreaming(text, baseMessages, ctrl);
      } else {
        await runNonStreaming(text, baseMessages, ctrl);
      }
      // 流式路径把 SSE 的 error 事件写进 state 而不是抛出，这里补一次检查。
      const streamed = errorRef.current;
      if (streamed) throw streamed;
      // 中止不算成功——runStreaming 循环中途检测到 ctrl.signal.aborted 会
      // 直接 return，和「流正常跑完」在这里长得一样（都不抛异常）。不单独
      // 判断的话，用户点 Stop 会被当成这一轮成功了，白白清掉仍然准确的
      // restoreFailed。
      if (ctrl.signal.aborted) return false;
      return true;
    } catch (e) {
      // 只有本次 attempt 自己的 controller 被中止才是用户点了 Stop。旧代码
      // 按 e.name === 'AbortError' 判断——client.ts 修复前，客户端自己的
      // 超时也会裸抛同名的 DOMException，和用户主动取消完全无法区分，超时
      // 的这一轮请求会被当成「用户取消」直接吞掉：没有气泡，没有 banner，
      // composer 照常解锁，用户的问题就此石沉大海。改成检查 ctrl 自己的
      // signal，和挂载恢复 effect 检查自己 ctrl.signal.aborted 的方式一致。
      if (ctrl.signal.aborted) return false;

      if (allowSilentRetry && classify(e).silentNewSession) {
        recordError(null);
        sessionIdRef.current = null;
        setSessionId(null);
        onSessionIdChange?.(null);
        // 防御性 filter：baseMessages 从来不会真的携带 streaming 条目——
        // 它只存在于 runStreaming 直接 setMessages 出去的 React state
        // 里，从不回写 messagesRef.current，而 baseMessages 正是从
        // messagesRef.current 派生的。真正摘除 streaming 气泡的是
        // send()/retry() 各自 finally 里那个对 state 做的 filter。这里
        // 留着只是以防万一，不是这一步在做实际清理工作。
        const clean = baseMessages.filter((m) => m.type !== 'streaming');
        messagesRef.current = clean;
        setMessages(clean);
        return attempt(text, clean, false);
      }

      handleFailure(e, text, baseMessages);
      return false;
    }
  }

  /**
   * 一次尝试彻底失败（不是可以静默重发的 SESSION_NOT_FOUND）之后要做什么：
   * 按错误分类决定是否弹 banner，以及是否在失败的用户消息后面补一条
   * `failed` 气泡——两者是正交的，分类表见 error-policy.ts。
   *
   * SESSION_NOT_FOUND 的分类把 banner 和 failedBubble 都设成 false——那是
   * 专门为「第一次出现、attempt() 正要静默重发」这一刻设计的：用户完全
   * 不该看到任何东西。可 handleFailure 只在静默重发已经用完（或压根不适用）
   * 时才会被调用；这时如果原样照抄同一条分类，用户的消息会人间蒸发——
   * 没有 bubble，也没有 banner，连重试的入口都没有。所以这里兜底：任何
   * 分类落到「两头都不可见」的失败，都强制退化成一条失败气泡，保证用户
   * 至少能看到消息失败了。
   */
  function handleFailure(e: unknown, text: string, baseMessages: ChatMessage[]): void {
    const action = classify(e);
    recordError(action.banner ? (e as Error) : null);

    if (!action.failedBubble && action.banner) return;

    // SESSION_NOT_FOUND 的 i18n 文案讲的是「即将自动开新会话」——那条承诺
    // 只对静默重试成立。走到这里说明静默重试已经用过、还是失败，原样
    // 展示同一条文案会重复一句已经落空的承诺，而且这条分类本身禁用重试
    // 按钮，用户无路可退。退化成 INTERNAL_ERROR：通用「出错了，请重试」
    // 文案 + 允许重试，给用户一个下一步。注意：不改 error-policy.ts 或
    // i18n 表里的 SESSION_NOT_FOUND 条目本身——静默重试那条路径仍然要用
    // 它，这里只是不把它用在「气泡展示」这一步上。
    const bubbleError =
      !action.failedBubble && !action.banner
        ? new ApiError(0, 'INTERNAL_ERROR', e instanceof Error ? e.message : 'Recovery failed')
        : e;

    // 防御性 filter，理由同 attempt() 静默重试分支里的那一处：baseMessages
    // 不会真的携带 streaming 条目，实际清理在 send()/retry() 的 finally
    // 里对 state 做。这里留着只是以防万一。
    const next = [
      ...baseMessages.filter((m) => m.type !== 'streaming'),
      makeFailedMessage(text, bubbleError),
    ];
    messagesRef.current = next;
    setMessages(next);
  }

  /**
   * 重试一条失败的消息。
   *
   * 先向后端拉一次历史探活——这既能确认 session 还在，也能发现
   * 「后端其实已经成功、只是响应在传输途中丢了」的情况，避免重复提问。
   * 探活的响应本身就是权威历史，顺手拿它重建消息列表，也修复了
   * 「消息其实发出去了，但 UI 没显示」的错位。
   */
  async function retry(failedMessageId: string): Promise<void> {
    const failed = messagesRef.current.find(
      (m) => m.id === failedMessageId && m.type === 'failed',
    );
    if (!failed || failed.type !== 'failed' || sending) return;

    setSending(true);
    recordError(null);

    // 失败气泡前面紧跟着的用户消息就是这次失败的原文（handleFailure 总是
    // 把失败气泡直接接在触发它的用户消息后面）。这里连它一起摘掉，而不是
    // 只摘失败气泡：下面要么用探活拉回来的服务端真相整个替换消息列表，
    // 要么重新 makeUserMessage(failed.text) 补一条——两条路径都会重新
    // 放回这句话，留着旧的只会让同一句话在对话里出现两次。
    const failedIndex = messagesRef.current.findIndex((m) => m.id === failedMessageId);
    const precedingUser = messagesRef.current[failedIndex - 1];
    const dropIndex =
      precedingUser?.type === 'user' && sameMessageText(precedingUser.text, failed.text)
        ? failedIndex - 1
        : -1;
    const withoutFailed = messagesRef.current.filter(
      (_, i) => i !== failedIndex && i !== dropIndex,
    );
    messagesRef.current = withoutFailed;
    setMessages(withoutFailed);

    try {
      const sid = sessionIdRef.current;
      // 探活成功后，服务端历史有没有已经把这句话记在最后——如果有（收到
      // 了，只是还没生成回复），下面就不能再重新 append 一条一模一样的
      // 本地用户气泡，否则和 restored 里那条服务端来的重复展示。默认为
      // false：没探活、探活失败、或探活拉回来的历史根本没有这句话，都要
      // 正常重新 append 一条。
      let skipAppend = false;
      // 探活被用户中途中止（点了 Stop）——和「探活失败但不是中止」是两回事：
      // 后者不阻断重发，前者必须整段放弃，既不能重发也不能报失败气泡。
      let probeAborted = false;
      // 声明在内层 try 外面，好让下面的 catch 也能读到它——判断「是不是
      // 用户中止」要看的是这个 controller 自己的 signal，不是 catch 到的
      // error 长什么样。
      let probeCtrl: AbortController | undefined;
      if (sid && historyApi) {
        try {
          // 探活自己的 controller，发布到 abortRef 上：探活期间 Composer
          // 显示的是 Stop（sending 为 true），必须真的能中止这次请求，
          // 不然按钮是死的。timeoutMs 复用挂载恢复同一个 5s 上限——不传
          // 的话会继承 client 默认的 60s，一次探活能把输入框锁死一分钟。
          probeCtrl = new AbortController();
          abortRef.current = probeCtrl;
          const response = await historyApi.getMessages(sid, {
            signal: probeCtrl.signal,
            timeoutMs: RESTORE_TIMEOUT_MS,
          });
          const { messages: restored, payload } = hydrateSession(response);
          setRestoredPayload(payload);

          if (alreadyAnswered(response, failed.text)) {
            // 后端已经答过了，直接展示，不重发。这次探活本身就是一次成功
            // 的历史加载——正是 restoreFailed 曾经失败的那个操作——所以
            // 顺手清掉它，不留一条过时的「无法加载对话」横幅。messagesRef
            // 此时已经不含这次在重试的 failed/user（retry() 开头摘掉了），
            // 但可能还挂着【另一条】不相关的失败消息——reattachPending
            // 把它挂回去，不然探活这一次「成功」反而会把它冲掉。
            const reconciled = reattachPending(restored, messagesRef.current);
            messagesRef.current = reconciled;
            setMessages(reconciled);
            setRestoreFailed(false);
            return;
          }

          // 同上：restored 是服务端权威历史，但按构造装不下别的失败消息——
          // reattachPending 负责把它们挂回来，不整个覆盖掉。
          const reconciled = reattachPending(restored, messagesRef.current);
          messagesRef.current = reconciled;
          setMessages(reconciled);

          const lastRestored = restored[restored.length - 1];
          skipAppend =
            lastRestored?.type === 'user' && sameMessageText(lastRestored.text, failed.text);
        } catch (probeError) {
          // 只有探活【自己的】 controller 被中止才是用户点了 Stop——client.ts
          // 修复前，探活自己的超时也会裸抛同名的 AbortError，和用户中止无法
          // 区分，会把「探活超时」误判成「用户放弃」，白白丢掉这条消息，见
          // attempt() 里同样的改法。
          if (probeCtrl?.signal.aborted) {
            probeAborted = true;
          } else if (isRestoreHopeless(probeError)) {
            // 探活本身就是对同一个 session 作用域 GET 的调用——404/410/422
            // 的结论和挂载恢复那边一样确定：这个 session 拉不出历史了，
            // 清掉本地 session_id，下面重发那一次不会再带上它。
            sessionIdRef.current = null;
            setSessionId(null);
            onSessionIdChange?.(null);
          }
          // 其它探活失败（网络仍然断）不阻断重发——直接试一次，失败会重新
          // 生成失败气泡。
        }
      }

      let base = messagesRef.current;
      if (!skipAppend) {
        base = [...messagesRef.current, makeUserMessage(failed.text)];
        messagesRef.current = base;
        setMessages(base);
      }
      // 探活被中止：和「发送本身被中止」同等对待——不生成新的失败气泡、
      // 不自动重发。真要重试，用户自己再点一次。但失败气泡（和它的重试
      // 按钮）在 retry() 一开始就被摘掉了——上面 `!skipAppend` 那步已经把
      // 触发这次重试的用户消息重新放回去了（探活挂起、从未 resolve，
      // skipAppend 一直是 false），这里把原来那条 failed 消息接在它后面
      // 补上，还原成和普通失败一样的形状：用户消息紧跟着失败气泡，而不是
      // 一条没有上下文的孤立气泡。
      if (probeAborted) {
        const back = [...messagesRef.current, failed];
        messagesRef.current = back;
        setMessages(back);
        return;
      }
      const ok = await attempt(failed.text, base, true);
      if (ok) setRestoreFailed(false);
    } finally {
      abortRef.current = null;
      setSending(false);
      setMessages((prev) => prev.filter((m) => m.type !== 'streaming'));
    }
  }

  async function runNonStreaming(
    text: string,
    messagesWithUser: ChatMessage[],
    ctrl: AbortController,
  ): Promise<void> {
    const currentSid = sessionIdRef.current;
    const response = await api.send(
      { message: text, ...(currentSid ? { session_id: currentSid } : {}) },
      { signal: ctrl.signal },
    );
    // it arrived live: the bubble types it out
    setMessages([...messagesWithUser, makeAssistantMessage(response, true)]);
    adoptSessionId(response.session_id);
  }

  /**
   * Takes the session id the backend answers with. A stream gives it with its first status
   * event already: kept from then on, a turn cut off midway can still be restored.
   */
  function adoptSessionId(sid: string | undefined): void {
    if (!sid || sid === sessionIdRef.current) return;
    setSessionId(sid);
    sessionIdRef.current = sid;
    onSessionIdChange?.(sid);
  }

  async function runStreaming(
    text: string,
    messagesWithUser: ChatMessage[],
    ctrl: AbortController,
  ): Promise<void> {
    const currentSid = sessionIdRef.current;
    const streamingMsg = makeStreamingMessage();
    setMessages([...messagesWithUser, streamingMsg]);
    const update = (change: (m: StreamingMessage) => StreamingMessage) =>
      setMessages((prev) =>
        prev.map((m) => (m.id === streamingMsg.id && m.type === 'streaming' ? change(m) : m)),
      );

    for await (const evt of streamApi!.stream(
      { message: text, ...(currentSid ? { session_id: currentSid } : {}) },
      { signal: ctrl.signal },
    )) {
      if (ctrl.signal.aborted) return;

      if (evt.event === 'status') {
        const { stage, session_id: sid } = evt.data;
        adoptSessionId(sid);
        update((m) => ({ ...m, stage }));
      } else if (evt.event === 'token') {
        const { delta } = evt.data;
        update((m) => ({ ...m, blocks: appendText(m.blocks, delta) }));
      } else if (evt.event === 'cards') {
        const { skus, products } = evt.data;
        update((m) => ({ ...m, blocks: [...m.blocks, { kind: 'cards', skus, products }] }));
      } else if (evt.event === 'done') {
        // Keep the local key stable: the bubble that has been typing the stream out carries
        // on with the authoritative response, which the backend says to show in its place.
        const assistantMsg = { ...makeAssistantMessage(evt.data, true), id: streamingMsg.id };
        const newMessages = [...messagesWithUser, assistantMsg];

        // flushSync forces React to commit synchronously. Without it,
        // setState inside `for await` can sometimes get bundled with the
        // subsequent setSending(false) from the finally block in a way
        // that causes the message list to not visually update until the
        // next user interaction. flushSync prevents that.
        flushSync(() => {
          setMessages(newMessages);
        });
        adoptSessionId(evt.data.session_id);
        return;
      } else if (evt.event === 'error') {
        // 流式错误走 HTTP 200 + event:error，ensureOk 抓不到它。这里手动
        // 包成 ApiError，否则 code 丢失、所有分码文案退化成通用兜底。
        // status 用 0 表示「HTTP 层没出错，错误来自 SSE 事件体」。
        const { code, message, request_id: requestId } = evt.data.error ?? {};
        recordError(
          new ApiError(0, code ?? 'INTERNAL_ERROR', message ?? 'Stream error', requestId),
        );
        return;
      }
    }

    // 跑到这里说明 for-await 自然跑完了——done / error 两个分支都会
    // return，中止也在循环开头 return 掉了，所以能落到循环外面只可能是
    // 一种情况：流在没有任何终止帧的前提下被干净地关闭了（后端这一路
    // spec 的约定是每条流都以 done 收尾）。不报错的话什么都不会抛出，
    // attempt() 会把它当成成功，finally 把流式气泡摘掉，用户的问题和
    // 已经收到的部分回复一起石沉大海，没有任何痕迹。
    if (!ctrl.signal.aborted) {
      recordError(new ApiError(0, 'INTERNAL_ERROR', 'Stream closed without a terminal event'));
    }
  }

  const retryAfterSeconds =
    error && 'retryAfterSeconds' in error
      ? ((error as { retryAfterSeconds?: number | null }).retryAfterSeconds ?? null)
      : null;

  return {
    messages,
    sessionId,
    initializing,
    sending,
    error,
    retryAfterSeconds,
    restoredPayload,
    restoreFailed,
    send,
    abort,
    reloadHistory,
    retry,
  };
}

function seedInitialMessages(disableOverviewEcho: boolean | undefined): ChatMessage[] {
  const head: ChatMessage[] = [makeWelcomeMessage()];
  if (!disableOverviewEcho) {
    const echo = readAndClearEcho();
    if (echo) head.push(makeEchoMessage(echo));
  }
  return head;
}

/**
 * 探活结果里最后一条 user 消息就是这次失败的原文，且其后已经有 assistant
 * 回复 —— 说明后端其实成功了，重发只会造成重复提问。
 */
function alreadyAnswered(response: SessionHistoryResponse, text: string): boolean {
  const ordered = [...(response.messages ?? [])].sort((a, b) => a.sequence_no - b.sequence_no);
  const lastUserIndex = ordered.map((m) => m.role).lastIndexOf('user');
  if (lastUserIndex === -1) return false;
  if (!sameMessageText(ordered[lastUserIndex].content, text)) return false;
  return ordered.slice(lastUserIndex + 1).some((m) => m.role === 'assistant');
}

/**
 * 后端可能对内容做过 trim（或客户端在等待响应期间没有 trim 用户输入）——
 * 纯字符串相等在这两处比较里都过于脆弱：一处是探活判断「是否已经回答」，
 * 一处是判断「本地/服务端历史末尾是不是已经有这句话」。统一按 trim 后
 * 比较，两处共用同一条规则。
 */
function sameMessageText(a: string, b: string): boolean {
  return a.trim() === b.trim();
}

/**
 * 服务端历史是权威的，但它按构造装不下纯客户端条目（failed 气泡、它前面
 * 那条没送达的 user 气泡、overview echo）——hydrateSession 只会吐出
 * welcome + 服务端行。用 restored 整个替换消息列表因此天生有损：任何
 * 「另一条还挂着重试按钮的失败消息」都会连原文一起被删掉，且失败从不
 * 落库，删了就再也拿不回来。
 *
 * 把 restored 里没有的本地未决条目挂在末尾——按文本匹配而不是整体合并，
 * 是因为本地列表里同样可能有后端其实已经收下、restored 里已经有对应
 * user 行的失败气泡（探活/重试成功的那些），原样拼接会让那些消息重复
 * 出现两遍。
 */
function reattachPending(restored: ChatMessage[], local: ChatMessage[]): ChatMessage[] {
  const serverTexts = new Set(
    restored
      .filter((m): m is Extract<ChatMessage, { type: 'user' }> => m.type === 'user')
      .map((m) => m.text.trim()),
  );
  const pending: ChatMessage[] = [];
  local.forEach((m, i) => {
    if (m.type === 'echo') {
      pending.push(m);
      return;
    }
    if (m.type !== 'failed' || serverTexts.has(m.text.trim())) return;
    const prev = local[i - 1];
    if (prev?.type === 'user' && sameMessageText(prev.text, m.text)) pending.push(prev);
    pending.push(m);
  });
  return pending.length === 0 ? restored : [...restored, ...pending];
}

type StreamingMessage = Extract<ChatMessage, { type: 'streaming' }>;

/** A token goes on the text block being written, or starts one after a group of cards. */
function appendText(blocks: StreamBlock[], md: string): StreamBlock[] {
  const last = blocks[blocks.length - 1];
  return last?.kind === 'text'
    ? [...blocks.slice(0, -1), { kind: 'text', md: last.md + md }]
    : [...blocks, { kind: 'text', md }];
}
